import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Typography,
  Space,
  Badge,
  Radio,
  Spin,
  Row,
  Col,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import ChainVisualization from "./ChainVisualization"; // Importing ChainVisualization

const { Text, Paragraph } = Typography;

const DefaultSimulation = () => {
  const [simulationData, setSimulationData] = useState(null);
  const [error, setError] = useState("");
  const [eligibleInstitutions, setEligibleInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [showEligibleInstitutions, setShowEligibleInstitutions] =
    useState(false);
  const [showAffectedChains, setShowAffectedChains] = useState(false);
  const [selectedChains, setSelectedChains] = useState([]);
  const [firstDefaultDone, setFirstDefaultDone] = useState(false);
  const [defaultingInstitution, setDefaultingInstitution] = useState(null);
  const [defaultType, setDefaultType] = useState("treasury");
  const [affectedChains, setAffectedChains] = useState([]);
  console.log("defaulting institution:", defaultingInstitution);
  console.log("current default type:", defaultType); // Log current default type for debugging

  const loadSimulation = useCallback(async () => {
    try {
      const response = await fetch("https://api.repo.tradingmechanism.com/api/init-simulation");
      const result = await response.json();

      if (result.status === "success") {
        setSimulationData(result);
        setShowEligibleInstitutions(false);
        setShowAffectedChains(false);
        setSelectedChains([]);
        setSelectedInstitution("");
        setFirstDefaultDone(false);
        setDefaultingInstitution(null);
        setAffectedChains([]);
        setDefaultType("treasury"); // Reset default type to initial value
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError("Error loading simulation: " + error.message);
    }
  }, []);
  useEffect(() => {
    loadSimulation();
  }, [loadSimulation]);

  // Modified getEligibleInstitutions
  const getEligibleInstitutions = async () => {
    try {
      // Log the default type being sent to the backend
      console.log(
        "Getting eligible institutions with default type:",
        defaultType
      );

      const response = await fetch(
        "https://api.repo.tradingmechanism.com/api/get-eligible-institutions",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ defaultType: defaultType }), // Use the current state value
        }
      );

      const result = await response.json();
      console.log("eligible inst", result);
      if (result.status === "success") {
        setEligibleInstitutions(result.eligibleInstitutions);
        setShowEligibleInstitutions(true);
        setShowAffectedChains(false);
        setAffectedChains([]);
        setSelectedChains([]);

        if (result.eligibleInstitutions.length > 0) {
          // Auto-select first institution but don't fetch affected chains yet
          setSelectedInstitution(result.eligibleInstitutions[0]);
          // Fetch affected chains for the auto-selected institution with the current default type
          getAffectedChainsForInstitution(
            result.eligibleInstitutions[0],
            defaultType
          );
        }
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError("Error getting eligible institutions: " + error.message);
    }
  };

  const handleInstitutionSelection = (institution) => {
    setSelectedInstitution(institution);
    // Pass the current defaultType state value to ensure consistency
    getAffectedChainsForInstitution(institution);
  };

  // When default type changes, clear selections to prevent inconsistencies
  const handleDefaultTypeChange = (e) => {
    const newDefaultType = e.target.value;
    console.log("Default type changed to:", newDefaultType);
    setDefaultType(newDefaultType);
    setShowEligibleInstitutions(false);
    setShowAffectedChains(false);
    setSelectedInstitution("");
    setSelectedChains([]);
    setAffectedChains([]);
  };

  const getAffectedChainsForInstitution = async (institution) => {
    if (!institution) {
      setShowAffectedChains(false);
      return;
    }

    try {
      console.log(
        "Getting affected chains for institution:",
        institution,
        "with default type:",
        defaultType // Use state directly
      );

      const response = await fetch(
        "https://api.repo.tradingmechanism.com/api/get-affected-chains",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            institution: institution,
            defaultType: defaultType, // Use state directly
          }),
        }
      );

      const result = await response.json();
      console.log("affected chains", result);
      if (result.status === "success") {
        setAffectedChains(result.chains);
        setShowAffectedChains(true);
        setSelectedChains([]);
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError("Error getting affected chains: " + error.message);
    }
  };

  const processDefault = async () => {
    if (selectedChains.length === 0) {
      showError("Please select at least one chain");
      return;
    }

    try {
      // Log the request to verify what's being sent
      console.log("Processing default with:", {
        institution: selectedInstitution,
        defaultType: defaultType,
        selectedChains,
        firstDefault: !firstDefaultDone,
      });

      const response = await fetch(
        "https://api.repo.tradingmechanism.com/api/process-default",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            institution: selectedInstitution,
            defaultType: defaultType, // Explicitly use the current default type
            selectedChains,
            firstDefault: !firstDefaultDone,
          }),
        }
      );
      const result = await response.json();
      if (result.status === "success") {
        setSimulationData(result);
        setShowEligibleInstitutions(false);
        setShowAffectedChains(false);
        setSelectedChains([]);
        setAffectedChains([]);

        // Save the defaulting institution on first default
        if (!firstDefaultDone) {
          setFirstDefaultDone(true);
          setDefaultingInstitution(selectedInstitution);
        }
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError("Error processing default: " + error.message);
    }
    console.log("simulation data", simulationData);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(""), 5000);
  };

  const getActiveChains = () => {
    return simulationData?.chains.filter((chain) => chain.active) || [];
  };

  const getInactiveChains = () => {
    return simulationData?.chains.filter((chain) => !chain.active) || [];
  };

  if (!simulationData) {
    return (
      <div className="text-center p-5">
        <Spin size="large" />
        <p>Loading simulation...</p>
      </div>
    );
  }

  const toggleChainSelection = (chainId) => {
    if (selectedChains.includes(chainId)) {
      setSelectedChains(selectedChains.filter((id) => id !== chainId));
    } else {
      setSelectedChains([...selectedChains, chainId]);
    }
  };
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <p>
        Head over to the "Visualization" tab to upload your own data file or
        reset to default sample data.
      </p>

      {error && (
        <Alert
          message={error}
          type="error"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      {!simulationData ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "48px 0",
          }}
        >
          {/* <Button type="primary" size="large" onClick={loadSimulation}>
            Start Simulation
          </Button> */}
        </div>
      ) : (
        <Row gutter={24}>
          {/* Left side - Chain Visualizations */}
          <Col xs={24} lg={15}>
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: 24 }}
            >
              <Card
                title="Affected Chains"
                bodyStyle={{
                  maxHeight: "600px", // Increased height
                  overflowY: "auto", // Enable vertical scrolling
                  padding: "16px",
                }}
              >
                <ChainVisualization
                  chains={getActiveChains()}
                  selectedNode={selectedInstitution}
                  selectedChains={selectedChains}
                  highlightNodesByInstitution={selectedInstitution} // Pass selected institution for highlighting
                />
                {getActiveChains().length === 0 && (
                  <Paragraph italic type="secondary" style={{ marginTop: 16 }}>
                    No active chains remaining.
                  </Paragraph>
                )}
              </Card>

              <Card
                title="Unaffected Chains"
                bodyStyle={{
                  maxHeight: "400px", // Reasonable height
                  overflowY: "auto", // Enable vertical scrolling
                  padding: "16px",
                }}
              >
                <ChainVisualization
                  chains={getInactiveChains()}
                  monochrome={true}
                  highlightNodesByInstitution={selectedInstitution} // Also highlight in inactive chains
                />
                {getInactiveChains().length === 0 && (
                  <Paragraph italic type="secondary" style={{ marginTop: 16 }}>
                    No inactive chains.
                  </Paragraph>
                )}
              </Card>
            </Space>
          </Col>

          {/* Right side - Controls - This remains largely the same */}
          <Col xs={24} lg={9}>
            <Space
              direction="vertical"
              style={{ width: "100%", marginBottom: 24 }}
            >
              <Card title="Process Default">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Default Type
                    </Text>
                    <Radio.Group
                      value={defaultType}
                      onChange={handleDefaultTypeChange} // Use the new handler
                      style={{ width: "100%" }}
                    >
                      <Radio.Button
                        value="treasury"
                        style={{ width: "50%", textAlign: "center" }}
                      >
                        Treasury Default
                      </Radio.Button>
                      <Radio.Button
                        value="money"
                        style={{ width: "50%", textAlign: "center" }}
                      >
                        Money Default
                      </Radio.Button>
                    </Radio.Group>
                  </div>

                  <Button block onClick={getEligibleInstitutions}>
                    Get Eligible Institutions
                  </Button>

                  {showEligibleInstitutions && (
                    <div>
                      <Text
                        strong
                        style={{ display: "block", marginBottom: 8 }}
                      >
                        Eligible Institutions:
                      </Text>
                      <Row gutter={[8, 8]}>
                        {eligibleInstitutions.map((institution) => (
                          <Col span={8} key={institution}>
                            <Button
                              type={
                                selectedInstitution === institution
                                  ? "primary"
                                  : "default"
                              }
                              onClick={() =>
                                handleInstitutionSelection(institution)
                              }
                              block
                            >
                              Institution {institution.toUpperCase()}
                            </Button>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {showAffectedChains && (
                    <div>
                      <Text
                        strong
                        style={{ display: "block", marginBottom: 8 }}
                      >
                        Affected Chains:
                      </Text>
                      <div
                        style={{
                          height: 240,
                          overflowY: "auto",
                          paddingRight: 16,
                        }}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          {affectedChains.map((chain) => (
                            <Button
                              key={chain.id}
                              type={
                                selectedChains.includes(chain.id)
                                  ? "primary"
                                  : "text"
                              }
                              onClick={() => toggleChainSelection(chain.id)}
                              block
                              style={{ textAlign: "left", padding: "8px 12px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: selectedChains.includes(
                                      chain.id
                                    )
                                      ? "#fff"
                                      : "#8c8c8c",
                                  }}
                                />
                                <span>{chain.nodes.join(" → ")}</span>
                              </div>
                            </Button>
                          ))}
                          {affectedChains.length === 0 && (
                            <Paragraph italic type="secondary">
                              No affected chains for this institution.
                            </Paragraph>
                          )}
                        </Space>
                      </div>

                      <Button
                        type="primary"
                        block
                        disabled={selectedChains.length === 0}
                        onClick={processDefault}
                        style={{ marginTop: 16 }}
                      >
                        Process Default
                      </Button>
                    </div>
                  )}
                </Space>
              </Card>

              <Card title="Default History">
                <div style={{ height: 200, overflowY: "auto" }}>
                  {simulationData.defaultHistory.length > 0 ? (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {simulationData.defaultHistory.map((entry, index) => (
                        <div
                          key={index}
                          style={{
                            padding: 8,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 8,
                            border: "1px solid #d9d9d9",
                          }}
                        >
                          <Badge
                            color="blue"
                            text={`Default ${index + 1}`}
                            style={{ marginBottom: 4 }}
                          />
                          <Text>{entry}</Text>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Paragraph italic type="secondary">
                      No defaults processed yet.
                    </Paragraph>
                  )}
                </div>
              </Card>
            </Space>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default DefaultSimulation;
